import type { Gender, Lakara, Pada, Purusha, Vacana } from '@/sanskrit/lib/sanskrit/types'

import { useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { cn } from '@/sanskrit/lib/utils'
import { useT } from '@/sanskrit/components/providers/app-state'
import { getDhatu } from '@/sanskrit/lib/sanskrit/dhatus'
import { getParadigm } from '@/sanskrit/lib/sanskrit/paradigms'
import { verbGlossEn, whoOf } from '@/sanskrit/lib/sanskrit/describe'
import { PURUSHAS, VACANAS } from '@/sanskrit/lib/sanskrit/types'
import { LAKARA_LABEL, PURUSHA_LABEL, VACANA_LABEL } from '@/sanskrit/lib/sanskrit/labels'
import { Segmented } from '@/sanskrit/components/vani/bits'
import AudioButton from '@/sanskrit/components/vani/audio-button'
import Skt from '@/sanskrit/components/vani/skt'

const PRONOUNS: Record<Purusha, Record<Vacana, string>> = {
    uttama: { eka: 'अहम्', dvi: 'आवाम्', bahu: 'वयम्' },
    madhyama: { eka: 'त्वम्', dvi: 'युवाम्', bahu: 'यूयम्' },
    prathama: { eka: 'सः', dvi: 'तौ', bahu: 'ते' }
}

const PRATHAMA_BY_GENDER: Record<Gender, Record<Vacana, string>> = {
    m: { eka: 'सः', dvi: 'तौ', bahu: 'ते' },
    f: { eka: 'सा', dvi: 'ते', bahu: 'ताः' },
    n: { eka: 'तत्', dvi: 'ते', bahu: 'तानि' }
}

/** Sentence-internal spelling: a final म् becomes anusvāra before a consonant. */
export const joinPhrase = (a: string, b: string): string => {
    if (a.endsWith('म्') && /^[क-ह]/.test(b)) return `${a.slice(0, -2)}ं ${b}`
    return `${a} ${b}`
}

export const pronounFor = (p: Purusha, v: Vacana, gender: Gender = 'm'): string => (p === 'prathama' ? PRATHAMA_BY_GENDER[gender][v] : PRONOUNS[p][v])

interface PurushaGridProps {
    dhatuId?: string
    lakara?: Lakara
    pada?: Pada
    mode?: 'pronoun' | 'verb'
    className?: string
}

/** The interactive 3 x 3 पुरुष × वचन grid. Every cell shows its pronoun, verb form and an example. */
export default function PurushaGrid({ dhatuId = 'path', lakara = 'lat', pada = 'P', className }: PurushaGridProps) {
    const t = useT()
    const [sel, setSel] = useState<{ p: Purusha; v: Vacana }>({ p: 'uttama', v: 'eka' })
    const [gender, setGender] = useState<Gender>('m')
    const d = getDhatu(dhatuId)
    const paradigm = useMemo(() => getParadigm(dhatuId, lakara, pada), [dhatuId, lakara, pada])
    if (!d || !paradigm) return null

    const rows: Purusha[] = ['uttama', 'madhyama', 'prathama']
    const form = paradigm.forms[sel.p][sel.v][0]
    const pronoun = pronounFor(sel.p, sel.v, gender)
    const sentence = `${joinPhrase(pronoun, form)}।`

    return (
        <div className={cn('space-y-4', className)}>
            <div className='flex flex-wrap items-center justify-between gap-2'>
                <p className='text-sm text-muted-foreground'>
                    <span className='deva font-semibold text-foreground'>{d.root}</span> ({t(d.meaning)}) · <span className='deva'>{LAKARA_LABEL[lakara].sa}</span>
                </p>
                <Segmented
                    size='sm'
                    label={t({ hi: 'प्रथम पुरुष का लिङ्ग', en: 'Gender for प्रथम पुरुष' })}
                    value={gender}
                    onChange={setGender}
                    options={[
                        { value: 'm', label: <span className='deva'>सः</span>, title: t({ hi: 'पुंलिङ्ग', en: 'masculine' }) },
                        { value: 'f', label: <span className='deva'>सा</span>, title: t({ hi: 'स्त्रीलिङ्ग', en: 'feminine' }) },
                        { value: 'n', label: <span className='deva'>तत्</span>, title: t({ hi: 'नपुंसकलिङ्ग', en: 'neuter' }) }
                    ]}
                />
            </div>

            <div className='overflow-x-auto'>
                <div className='grid min-w-[20rem] grid-cols-[auto_repeat(3,minmax(0,1fr))] gap-2'>
                    <div />
                    {VACANAS.map(v => (
                        <div key={v} className='px-1 text-center'>
                            <p lang='sa' className='deva text-sm font-semibold text-foreground'>{VACANA_LABEL[v].sa}</p>
                            <p className='text-xs text-muted-foreground'>{t({ hi: VACANA_LABEL[v].hi, en: VACANA_LABEL[v].en })}</p>
                        </div>
                    ))}
                    {rows.map(p => (
                        <div key={p} className='contents'>
                            <div className='flex flex-col justify-center pr-2 text-right'>
                                <p lang='sa' className='deva text-sm font-semibold text-foreground'>{PURUSHA_LABEL[p].short}</p>
                                <p className='text-[0.7rem] text-muted-foreground'>{t({ hi: p === 'uttama' ? 'मैं/हम' : p === 'madhyama' ? 'तुम' : 'वह/वे', en: PURUSHA_LABEL[p].en })}</p>
                            </div>
                            {VACANAS.map(v => {
                                const active = sel.p === p && sel.v === v
                                return (
                                    <button
                                        key={v}
                                        type='button'
                                        onClick={() => setSel({ p, v })}
                                        aria-pressed={active}
                                        className={cn(
                                            'rounded-2xl border px-2 py-3 text-center transition-all',
                                            active ? 'border-saffron-strong bg-saffron text-ink shadow-md' : 'border-border bg-card hover:border-saffron hover:bg-saffron-soft'
                                        )}
                                    >
                                        <span lang='sa' className='deva block text-lg font-bold sm:text-xl'>{pronounFor(p, v, gender)}</span>
                                        <span lang='sa' className={cn('deva block text-sm sm:text-base', active ? 'text-ink' : 'text-saffron-strong')}>{paradigm.forms[p][v][0]}</span>
                                    </button>
                                )
                            })}
                        </div>
                    ))}
                </div>
            </div>

            <motion.div key={`${sel.p}-${sel.v}-${gender}`} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className='rounded-2xl border border-border bg-card p-4 shadow-sm'>
                <div className='flex flex-wrap items-start justify-between gap-3'>
                    <div>
                        <Skt text={sentence} className='text-3xl font-bold text-ink' iast='auto' />
                        <p className='mt-1 text-muted-foreground'>
                            {t({ hi: `${whoOf(sel.p, sel.v).hi} · ${d.meaning.hi} · ${LAKARA_LABEL[lakara].hi}`, en: verbGlossEn({ dhatuId, lakara, purusha: sel.p, vacana: sel.v }) })}
                        </p>
                    </div>
                    <AudioButton text={sentence} />
                </div>
                <dl className='mt-3 grid gap-2 sm:grid-cols-3'>
                    <div className='rounded-xl bg-muted/60 px-3 py-2'>
                        <dt className='text-xs text-muted-foreground'>{t({ hi: 'पुरुष', en: 'Person' })}</dt>
                        <dd className='deva font-semibold'>{PURUSHA_LABEL[sel.p].sa}</dd>
                        <dd className='text-xs text-muted-foreground'>{t(PURUSHA_LABEL[sel.p].desc)}</dd>
                    </div>
                    <div className='rounded-xl bg-muted/60 px-3 py-2'>
                        <dt className='text-xs text-muted-foreground'>{t({ hi: 'वचन', en: 'Number' })}</dt>
                        <dd className='deva font-semibold'>{VACANA_LABEL[sel.v].sa}</dd>
                        <dd className='text-xs text-muted-foreground'>{t(VACANA_LABEL[sel.v].desc)}</dd>
                    </div>
                    <div className='rounded-xl bg-muted/60 px-3 py-2'>
                        <dt className='text-xs text-muted-foreground'>{t({ hi: 'क्रिया', en: 'Verb' })}</dt>
                        <dd className='deva font-semibold'>{form}</dd>
                        <dd className='text-xs text-muted-foreground'>{t({ hi: `${d.root}, ${LAKARA_LABEL[lakara].hi}`, en: `${d.root}, ${LAKARA_LABEL[lakara].en.toLowerCase()}` })}</dd>
                    </div>
                </dl>
            </motion.div>
        </div>
    )
}
